import React, { useRef } from 'react';
import { Button, Box } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface PatchType {
  sid: string;
  sourceHome: string;
  targetHome: string;
  patchFolder: string;
  platform: string;
  patchOptions: {
    recommended: boolean;
    ru: boolean;
    ruVersion: string;
    ojvm: boolean;
    ojvmVersion: string;
    opatch: boolean;
    dpbp: boolean;
    jdk: boolean;
    specificPatches: string;
    ocw?: boolean;
    ocwVersion: string;
  };
  download: 'NO' | 'YES';
  restoration: boolean;
  tdeEnabled: boolean;
  useProxy: boolean;
  proxyHost: string;
  proxyPort: string;
  upgradeType: string;
  distributedUpgrade: boolean;
  targetCdbSid: string;
  newPdbName: string;
  fileNameConvertOption: string;
  sourcePath: string;
  targetPath: string;
  sourcePdb: string;
  targetPdbName: string;
  dbLink: string;
  refreshOption: string;
  refreshInterval: string;
  sourceTnsAdminEnabled: boolean;
  sourceTnsAdminDir: string;
  targetTnsAdminEnabled: boolean;
  targetTnsAdminDir: string;
  upgradeNodeEnabled: boolean;
  upgradeNodeValue: string;
  editionEnabled?: boolean;
  edition?: string;
  oracleBaseEnabled?: boolean;
  oracleBase?: string;
  targetVersionEnabled?: boolean;
  targetVersion?: string;
  startTimeType?: 'datetime' | 'now' | 'delay';
  startTimeValue?: string;
  dropGrpAfterUpgrade: boolean;
  dropGrpAfterPatching: boolean;
  timezoneUpgUpg: boolean;
  runUtlrpUpg: boolean;
  removeUnderscoreUpg: boolean;
  raiseCompatibleUpg: boolean;
  raiseCompatibleUpgValue: string;
  delDuringUpgradePfile: string;
  addAfterUpgradePfile: string;
  delDuringUpgradePfileEnabled: boolean;
  addAfterUpgradePfileEnabled: boolean;
  proactiveFixups: boolean;
  sourceHomeEnabled: boolean;
  addDuringUpgradePfile: string;
  addDuringUpgradePfileEnabled: boolean;
  delAfterUpgradePfile: string;
  delAfterUpgradePfileEnabled: boolean;
  emTargetNameEnabled: boolean;
  emTargetName: string;
  emcliPathEnabled: boolean;
  emcliPath: string;
  emBlackoutSuffixEnabled: boolean;
  emBlackoutSuffix: string;
  oraInventoryEnabled?: boolean;
  oraInventory?: string;
  inventoryGroupEnabled?: boolean;
  inventoryGroup?: string;
  binaryOptionRac?: boolean;
  clusterNodesEnabled?: boolean;
  clusterNodes?: string;
}

interface LoadConfigFileProps {
  onConfigLoaded: (data: {
    globalLogDir: string;
    keystorePath: string;
    patches: PatchType[];
    operationType: string;
    executionMode: string;
    tabList: { key: string; label: string }[];
  }) => void;
  buttonOnly?: boolean;
}

const parsePatchOptions = (patchValue: string) => {
  const options = {
    recommended: false,
    ru: false,
    ruVersion: '',
    ojvm: false,
    ojvmVersion: '',
    opatch: false,
    dpbp: false,
    jdk: false,
    specificPatches: '',
    ocw: false,
    ocwVersion: '',
  };

  if (patchValue === 'RECOMMENDED') {
    options.recommended = true;
    return options;
  }

  const parts = patchValue.split(',');
  parts.forEach(part => {
    const trimmed = part.trim();
    if (trimmed.startsWith('RU:')) {
      options.ru = true;
      options.ruVersion = trimmed.substring(3);
    } else if (trimmed === 'RU') {
      options.ru = true;
    } else if (trimmed.startsWith('OJVM:')) {
      options.ojvm = true;
      options.ojvmVersion = trimmed.substring(5);
    } else if (trimmed === 'OJVM') {
      options.ojvm = true;
    } else if (trimmed === 'OPATCH') {
      options.opatch = true;
    } else if (trimmed.startsWith('OCW:')) {
      options.ocw = true;
      options.ocwVersion = trimmed.substring(4);
    } else if (trimmed === 'OCW') {
      options.ocw = true;
    } else if (trimmed === 'DPBP') {
      options.dpbp = true;
    } else if (trimmed === 'JDK') {
      options.jdk = true;
    } else if (trimmed) {
      options.specificPatches = trimmed;
    }
  });

  return options;
};

const LoadConfigFile: React.FC<LoadConfigFileProps> = ({ onConfigLoaded, buttonOnly = false }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (!content.startsWith('# Created by AutoUpgrade Composer')) {
        alert('Invalid config file format');
        return;
      }

      const lines = content.split('\n');
      let operationType = 'patch';
      let executionMode = 'deploy';
      let upgType = '';
      let fileNameConvertOption = '';
      let refreshOption = '';
      if (lines.length > 1) {
        if (lines[1].startsWith('# Patch, ExecMode:')) {
          operationType = 'patch';
          const match = lines[1].match(/ExecMode: ([^,\n]+)/);
          if (match) {
            const mode = match[1].trim().toLowerCase();
            if (mode === 'deploy') executionMode = 'deploy';
            else if (mode === 'analyze') executionMode = 'analyze';
            else if (mode === 'fixups') executionMode = 'fixups';
            else if (mode === 'download') executionMode = 'download';
            else if (mode === 'createhome') executionMode = 'create_home';
          }
        } else if (lines[1].startsWith('# Upgrade, UpgType:')) {
          operationType = 'upgrade';
          const upgMatch = lines[1].match(/UpgType: ([^,\n]+)/);
          if (upgMatch) {
            upgType = upgMatch[1].trim();
            executionMode = upgType;
          }
          const fileNameMatch = lines[1].match(/FileNameConvert: ([^,\n]+)/);
          if (fileNameMatch) {
            fileNameConvertOption = fileNameMatch[1].trim();
            if (fileNameConvertOption === 'paths') fileNameConvertOption = 'convert';
          }
          const refreshMatch = lines[1].match(/Refresh: ([^,\n]+)/);
          if (refreshMatch) {
            refreshOption = refreshMatch[1].trim();
          }
        }
      }

      const tabDict: { [tab: string]: { [field: string]: string } } = {};
      const globalConfig: { [key: string]: string } = {};
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;
        if (trimmed.startsWith('global.')) {
          const [gk, gv] = trimmed.split('=');
          globalConfig[gk.replace('global.', '')] = gv;
          continue;
        }
        const match = trimmed.match(/^([^.]+)\.(.+?)=(.*)$/);
        if (!match) continue;
        const [, tab, field, value] = match;
        if (!tabDict[tab]) tabDict[tab] = {};
        tabDict[tab][field] = value;
      }

      const patches: PatchType[] = Object.entries(tabDict).map(([tab, fields]) => {
        const patch: PatchType = {
          sid: '',
          sourceHome: '',
          targetHome: '',
          patchFolder: '',
          platform: 'LINUX.X64',
          patchOptions: {
            recommended: false,
            ru: true,
            ruVersion: '',
            ojvm: false,
            ojvmVersion: '',
            opatch: true,
            dpbp: false,
            jdk: false,
            specificPatches: '',
            ocwVersion: '',
          },
          download: 'NO',
          restoration: true,
          tdeEnabled: false,
          useProxy: false,
          proxyHost: '',
          proxyPort: '',
          upgradeType: '',
          distributedUpgrade: false,
          targetCdbSid: '',
          newPdbName: '',
          fileNameConvertOption: '',
          sourcePath: '',
          targetPath: '',
          sourcePdb: '',
          targetPdbName: '',
          dbLink: '',
          refreshOption: '',
          refreshInterval: '',
          sourceTnsAdminEnabled: false,
          sourceTnsAdminDir: '',
          targetTnsAdminEnabled: false,
          targetTnsAdminDir: '',
          upgradeNodeEnabled: false,
          upgradeNodeValue: '',
          editionEnabled: false,
          edition: '',
          oracleBaseEnabled: false,
          oracleBase: '/u01/app/oracle',
          targetVersionEnabled: true,
          targetVersion: '',
          startTimeType: undefined,
          startTimeValue: undefined,
          dropGrpAfterUpgrade: false,
          dropGrpAfterPatching: false,
          timezoneUpgUpg: true,
          runUtlrpUpg: true,
          removeUnderscoreUpg: true,
          raiseCompatibleUpg: false,
          raiseCompatibleUpgValue: '',
          delDuringUpgradePfile: '',
          addAfterUpgradePfile: '',
          delDuringUpgradePfileEnabled: false,
          addAfterUpgradePfileEnabled: false,
          proactiveFixups: false,
          sourceHomeEnabled: false,
          addDuringUpgradePfile: '',
          addDuringUpgradePfileEnabled: false,
          delAfterUpgradePfile: '',
          delAfterUpgradePfileEnabled: false,
          emTargetNameEnabled: false,
          emTargetName: '',
          emcliPathEnabled: false,
          emcliPath: '',
          emBlackoutSuffixEnabled: false,
          emBlackoutSuffix: '',
          oraInventoryEnabled: false,
          oraInventory: '',
          inventoryGroupEnabled: false,
          inventoryGroup: '',
          binaryOptionRac: false,
          clusterNodesEnabled: false,
          clusterNodes: '',
        };
        for (const [field, value] of Object.entries(fields)) {
          switch (field) {
            case 'sid': patch.sid = value; break;
            case 'source_home':
              patch.sourceHome = value;
              patch.sourceHomeEnabled = true;
              break;
            case 'target_home': patch.targetHome = value; break;
            case 'folder': patch.patchFolder = value; break;
            case 'platform': patch.platform = value; break;
            case 'patch': patch.patchOptions = parsePatchOptions(value); break;
            case 'download': patch.download = value as 'NO' | 'YES'; break;
            case 'restoration': patch.restoration = value === 'YES'; break;
            case 'proxy_host': patch.proxyHost = value; patch.useProxy = true; break;
            case 'proxy_port': patch.proxyPort = value; patch.useProxy = true; break;
            case 'upgrade_type': patch.upgradeType = value; break;
            case 'tune_setting':
              patch.proactiveFixups = value.includes('proactive_fixups=true');
              patch.distributedUpgrade = value.includes('distributed_upgrade=yes');
              break;
            case 'upgrade_node': patch.upgradeNodeEnabled = true; patch.upgradeNodeValue = value; break;
            case 'target_cdb': patch.targetCdbSid = value; break;
            case 'target_pdb_name': patch.newPdbName = value; break;
            case 'target_pdb_name.' + (patch.sourcePdb || ''): patch.targetPdbName = value; break;
            case 'pdbs': patch.sourcePdb = value; break;
            case 'source_dblink.' + (patch.sourcePdb || ''):
              const [dbLink, refresh] = value.split(' ');
              patch.dbLink = dbLink;
              if (refresh && refresh !== 'CLONE_ONLY') {
                const seconds = Number(refresh);
                patch.refreshOption = 'interval';
                patch.refreshInterval = !isNaN(seconds) && seconds > 0 ? String(Math.round(seconds / 60)) : '';
              } else if (refresh === 'CLONE_ONLY') {
                patch.refreshOption = 'clone_only';
                patch.refreshInterval = '';
              }
              break;
            case 'source_tns_admin_dir': patch.sourceTnsAdminEnabled = true; patch.sourceTnsAdminDir = value; break;
            case 'target_tns_admin_dir': patch.targetTnsAdminEnabled = true; patch.targetTnsAdminDir = value; break;
            case 'tde_enabled': patch.tdeEnabled = value === 'YES'; break;
            case 'home_settings.edition': patch.edition = value; patch.editionEnabled = true; break;
            case 'home_settings.oracle_base': patch.oracleBase = value; patch.oracleBaseEnabled = true; break;
            case 'home_settings.inventory_location': patch.oraInventory = value; patch.oraInventoryEnabled = true; break;
            case 'home_settings.inventory_group': patch.inventoryGroup = value; patch.inventoryGroupEnabled = true; break;
            case 'home_settings.binopt.rac': patch.binaryOptionRac = value === 'yes'; break;
            case 'home_settings.cluster_nodes': patch.clusterNodes = value; patch.clusterNodesEnabled = true; break;
            case 'target_version': patch.targetVersion = value; patch.targetVersionEnabled = true; break;
            case 'start_time': patch.startTimeType = 'datetime'; patch.startTimeValue = value; break;
            case 'drop_grp_after_upgrade': patch.dropGrpAfterUpgrade = value === 'YES'; break;
            case 'drop_grp_after_patching': patch.dropGrpAfterPatching = value === 'YES'; break;
            case 'timezone_upg': patch.timezoneUpgUpg = value !== 'NO'; break;
            case 'run_utlrp': patch.runUtlrpUpg = value !== 'NO'; break;
            case 'remove_underscore_parameters': patch.removeUnderscoreUpg = value !== 'NO'; break;
            case 'raise_compatible':
              patch.raiseCompatibleUpg = true;
              patch.raiseCompatibleUpgValue = value !== 'YES' ? value : '';
              break;
            case 'del_during_upgrade_pfile': patch.delDuringUpgradePfile = value; patch.delDuringUpgradePfileEnabled = !!value; break;
            case 'add_after_upgrade_pfile': patch.addAfterUpgradePfile = value; patch.addAfterUpgradePfileEnabled = !!value; break;
            case 'add_during_upgrade_pfile': patch.addDuringUpgradePfile = value; patch.addDuringUpgradePfileEnabled = !!value; break;
            case 'del_after_upgrade_pfile': patch.delAfterUpgradePfile = value; patch.delAfterUpgradePfileEnabled = !!value; break;
            case 'em_target_name':
              patch.emTargetName = value;
              patch.emTargetNameEnabled = !!value;
              break;
            case 'emcli_path':
              patch.emcliPath = value;
              patch.emcliPathEnabled = !!value;
              break;
            case 'em_blackout_suffix':
              patch.emBlackoutSuffix = value;
              patch.emBlackoutSuffixEnabled = !!value;
              break;
            default:
              if (field.startsWith('target_pdb_copy_option.')) {
                if (value === 'file_name_convert=NONE') {
                  patch.fileNameConvertOption = 'none';
                  patch.sourcePath = '';
                  patch.targetPath = '';
                } else if (value.startsWith("file_name_convert=('")) {
                  patch.fileNameConvertOption = 'convert';
                  const match = value.match(/\('(.+)', '(.+)'\)/);
                  if (match) {
                    patch.sourcePath = match[1];
                    patch.targetPath = match[2];
                  }
                } else if (value.startsWith('file_name_convert')) {
                  patch.fileNameConvertOption = 'nocopy';
                }
              }
              break;
          }
        }
        patch.upgradeType = upgType || patch.upgradeType || 'regular';
        if (!patch.fileNameConvertOption) patch.fileNameConvertOption = fileNameConvertOption || '';
        if (!patch.refreshOption && upgType === 'pdbrelocation') patch.refreshOption = refreshOption || '';
        // Detect Windows platform from any Windows-style path
        const isWindowsPath = (str: string) => typeof str === 'string' && /^[A-Za-z]:\\/.test(str);
        if (
          isWindowsPath(patch.sourceHome) ||
          isWindowsPath(patch.targetHome) ||
          isWindowsPath(patch.patchFolder) ||
          isWindowsPath(patch.oracleBase || '')
        ) {
          patch.platform = 'WINDOWS.X64';
        }
        return patch;
      });

      const tabList = Object.keys(tabDict).map(tab => ({ key: tab, label: tab }));
      onConfigLoaded({
        globalLogDir: globalConfig.global_log_dir || '',
        keystorePath: globalConfig.keystore || '',
        patches,
        operationType,
        executionMode,
        tabList,
      });
    };

    reader.readAsText(file);
  };

  if (buttonOnly) {
    return (
      <>
        <input
          type="file"
          accept=".cfg"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileSelect}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            fontWeight: 'bold',
            borderRadius: 8,
            px: 4,
            py: 1.5,
            fontSize: 16,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          }}
        >
          Load Configuration
        </Button>
      </>
    );
  }
  return (
    <>
      <input
        type="file"
        accept=".cfg"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelect}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            fontWeight: 'bold',
            borderRadius: 8,
            px: 4,
            py: 1.5,
            fontSize: 16,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          }}
        >
          Load Configuration
        </Button>
      </Box>
    </>
  );
};

export const LoadConfigButton: React.FC<{ onConfigLoaded?: (data: any) => void; sx?: any }> = ({ onConfigLoaded, sx }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onConfigLoaded) {
      // Delegate to parent if provided, otherwise do nothing
      // (You can move the parsing logic here if needed)
    }
  };
  return (
    <>
      <input
        type="file"
        accept=".cfg"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelect}
      />
      <Button
        variant="contained"
        color="primary"
        startIcon={<UploadFileIcon />}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          fontWeight: 'bold',
          borderRadius: 8,
          px: 4,
          py: 1.5,
          fontSize: 16,
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          ...sx,
        }}
      >
        Load Configuration
      </Button>
    </>
  );
};

export default LoadConfigFile; 